import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { calculateProposal } from "../services/rateCardService";
import {
  useSegments, useRenderedTemplate, useProposalPackages, useProposalDefaults,
} from "../hooks/useRateCard";
import { PROPOSAL_CSS } from "./proposalTheme";
import ProposalDocument from "./ProposalDocument";
import QuotationDocument from "./QuotationDocument";
import SendModal from "./SendModal";
import { buildDocumentHtml } from "./documentHtml";
import { missingClientFields, inr, applyCounts } from "./documentBasics";
import AttorneyNumbers from "./AttorneyNumbers";
import ExistingCustomerNotice from "./ExistingCustomerNotice";
import { useCustomerLookup } from "../hooks/useCustomerLookup";
import { COUNTRY_CODES } from "../utils/phones";

/**
 * Create a proposal or a quotation.
 *
 * The form on the left, the finished document on the right. Every price comes
 * from POST /proposals/calculate and every other word from /proposal-template:
 * nothing about the quote is computed or authored in the browser, so a rate card
 * edit changes what reps quote immediately.
 *
 * Sending posts the rendered HTML to /proposals/sends, which both emails it and
 * logs it — so what the rep previewed is what the client receives.
 */

const DEBOUNCE_MS = 350;

const EMPTY_FORM = {
  segment: "corporate",
  /* Who this quote belongs to, when it came from somewhere. A lead carries its
     id across; a customer is recognised by the lookup. Both stay null on a quote
     typed from scratch for someone new. */
  lead_id: null,
  customer_id: null,
  client_name: "",
  contact_name: "",
  email: "",
  phone: "",
  // Leads come from anywhere; a bare number is ambiguous without this.
  country_code: "+91",
  gstin: "",
  address: "",
  // Optional: the marks count can be filled in from these instead of typed.
  attorney_numbers: [],
  /* Left empty on purpose. The volume a band covers is the rate card's to state,
     so the opening figure is read off the bands once they load — a number typed
     in here would be a second, silently wrong copy of them. */
  marks: "",
  package: "auto",
  users: 1,
  clients: 0,
  frequency: "quarterly",
  additional_discount: 0,
  onboarding_discount: 0,
  /* Rep-set price per trademark (₹, the segment's native unit). Blank = the rate
     card's own rate; it can only be raised above the card minimum, never below. */
  manual_rate: "",
  /* Which plan features to include in the document and grant on activation.
     null = all of them (the default); becomes an array once the rep unticks any. */
  selected_feature_ids: null,
  /* No start date. The subscription runs from the day the payment completes, so
     there is nothing here for a rep to decide — a field that cannot change the
     outcome only invites them to believe it can. */
  /* Internal subscription settings. They travel with the quote and are stored on
     the subscription when it activates, but the client never sees them — how
     long we wait past expiry is our business, not part of the offer. */
  /* How long the payment link stays payable, in hours. Blank rather than a
     number: the default belongs to the server, and a figure invented here would
     quietly override it on every quote a rep never touched this field on. */
  payment_link_expiry_hours: "",
  grace_period_days: 7,
  reminder_before_days: 7,
  notes: "",
};

/**
 * The volume a band stands for, as the rate card states it.
 *
 * "Business — up to 1,000" means a thousand, so that is the figure to put in the
 * field: quoting 800 against it would price the band correctly but tell the
 * client they were buying less than the band covers. An open-ended top band has
 * no ceiling to read, so its floor is the only honest number.
 */
/**
 * Hours as the server states them, with the days they come to when they divide
 * evenly — "720 hours / 30 days" reads as one thing, where either number alone
 * makes the reader do the arithmetic. Nothing is rounded: 100 hours is not four
 * days, so it stays 100 hours.
 */
const hoursLabel = (hours) => {
  const h = Number(hours);
  if (!Number.isFinite(h) || h <= 0) return "";
  const unit = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
  return h % 24 === 0
    ? `${unit(h, "hour")} / ${unit(h / 24, "day")}`
    : unit(h, "hour");
};

const bandMarks = (band) => {
  const max = Number(band?.max_marks);
  if (Number.isFinite(max) && max > 0) return max;
  const min = Number(band?.min_marks);
  return Number.isFinite(min) && min > 0 ? min : null;
};

/**
 * One screen per document: the route decides which, so a rep is never a click
 * away from sending the wrong one. The form and the pricing are shared; only the
 * rendered document differs.
 *
 * @param {"proposal"|"quotation"} doc
 */
const CreateProposal = ({ doc = "proposal" }) => {
  const isQuotation = doc === "quotation";
  /* Opened from the log, the row carries the inputs it was sent with — so the
     same document can be rebuilt rather than reconstructed from memory. Unknown
     keys are ignored: an older payload must not resurrect a retired field. */
  const { state } = useLocation();
  const [form, setForm] = useState(() => {
    const saved = state?.payload;
    if (!saved || typeof saved !== "object") return { ...EMPTY_FORM };
    const known = Object.fromEntries(
      Object.entries(saved).filter(([k]) => k in EMPTY_FORM),
    );
    return { ...EMPTY_FORM, ...known };
  });
  const [quote, setQuote] = useState(null);
  /* What each attorney number holds, as looked up while the rep typed. Sent with
     the quote so the customer's numbers can be stored on acceptance without
     asking the registry again. */
  const [attorneyTrademarks, setAttorneyTrademarks] = useState([]);
  const [calcError, setCalcError] = useState("");
  const [calculating, setCalculating] = useState(false);
  /* The rate-card floor for the selected plan, learned from the last good quote.
     The rep may price at or above it, never below. Held in state so it survives
     the calculate calls we skip while a below-minimum figure is on screen. */
  const [rateMin, setRateMin] = useState(null);
  const [rateUnit, setRateUnit] = useState(null);

  const { data: segments = [] } = useSegments();
  /* Both documents come back print-ready: sections ordered, enabled-only, and
     resolved for this segment. The proposal fills pages 1-3; the quotation is
     asked for separately because page 4 has its own section list. */
  const { data: template, isLoading: templateLoading } = useRenderedTemplate(doc, form.segment);
  /* A proposal carries its own quotation so the server can send one the moment
     the client accepts — without it the client would get a generic document
     built from figures nobody reviewed. Only fetched on the proposal screen;
     the quotation screen already has this as its main document. */
  const { data: quotationTemplate } = useRenderedTemplate(
    isQuotation ? null : "quotation",
    form.segment,
  );
  const { data: packages = [] } = useProposalPackages(form.segment);
  /* Shares the packages call — see useProposalDefaults. */
  const { data: serverDefaults } = useProposalDefaults(form.segment);
  const defaultExpiryHours = serverDefaults?.paymentLinkExpiryHours;
  /* Whether this recipient is already on the books. Shown while there is still
     a choice to make — after payment the consequence is already decided. */
  const { data: lookup } = useCustomerLookup({ email: form.email, phone: form.phone });
  const existingCustomer = lookup?.exists ? lookup.customer : null;

  const debounceRef = useRef(null);
  const requestRef = useRef(0);
  const docRef = useRef(null);
  const quotationRef = useRef(null);
  /* Whether the rep has set these themselves. Untouched, they follow whatever
     the quoted band includes; once typed in, that number is theirs to keep. */
  const seatsTouched = useRef(false);
  const clientsTouched = useRef(false);
  /* Same idea for the count, with one addition: a document reopened from the log
     already carries the volume it was sent at, and that is the rep's number —
     the band's own figure must not overwrite it. */
  const marksTouched = useRef(Boolean(Number(state?.payload?.marks)));

  /* The admin shell sets `overflow: hidden` on .main-content, and any ancestor
     with a clipping overflow disables position:sticky outright — which is why
     the form panel never followed the scroll. Lifted only while this screen is
     mounted, and put back on the way out so no other page is affected. */
  useEffect(() => {
    const shell = document.querySelector(".main-content");
    if (!shell) return undefined;
    const previous = shell.style.overflow;
    shell.style.overflow = "visible";
    return () => { shell.style.overflow = previous; };
  }, []);
  const [copied, setCopied] = useState(false);
  /* Which channel the modal opens on — "email" from Send, "download" from the
     PDF button. Null when it is closed. Both go through the same modal because
     both produce the same server-rendered document. */
  const [sending, setSending] = useState(null);

  /* The whole page, stylesheet included — the same string the email carries.
     A bare fragment pastes into a mail client as unstyled text. */
  const copyHtml = async () => {
    if (!docRef.current) return;
    try {
      await navigator.clipboard.writeText(buildDocumentHtml(docRef.current, doc));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is blocked outside a secure context; nothing useful to say.
    }
  };

  const currentSegment = segments.find((x) => x.slug === form.segment);
  /* A client book and attorney numbers both belong to the attorney-style
     segments — corporate is `is_attorney: 0` and has neither. The trademark
     count still applies there; it is just typed rather than looked up. */
  const isAttorneySegment = Boolean(currentSegment && Number(currentSegment.is_attorney) !== 0);
  const usesClients = isAttorneySegment;

  /* The volume this segment actually covers, read off its own bands rather than
     kept as a second copy of them. */
  const bands = packages.filter((x) => x.value !== "auto");
  const minMarks = bands.length
    ? Math.min(...bands.map((x) => Number(x.min_marks) || 0))
    : null;
  const maxMarks = bands.length && bands.every((x) => x.max_marks != null)
    ? Math.max(...bands.map((x) => Number(x.max_marks)))
    : null;

  /* Opening defaults, filled once the bands load: the smallest plan selected and
     its own count seeded. Auto-suggest is gone — the rep picks the plan and the
     count by hand, and the plan no longer follows the count. This only fills a
     blank count and replaces the retired "auto" sentinel; a plan or count already
     on screen (typed, picked, or reopened from the log) is left alone. */
  useEffect(() => {
    if (!bands.length) return;
    const smallest = [...bands].sort(
      (a, b) => (Number(a.min_marks) || 0) - (Number(b.min_marks) || 0),
    )[0];
    const patch = {};
    if (form.package === "auto") patch.package = smallest.value;
    if (!marksTouched.current && !Number(form.marks)) {
      const seed = bandMarks(smallest);
      if (seed != null) patch.marks = seed;
    }
    if (Object.keys(patch).length) set(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages]);

  /**
   * Picking a band sets the count it covers.
   *
   * "SME Pack — up to 100" quoted against 10,000 marks prices one thing and
   * prints another, and the client reads the printed number. So the pick wins
   * over whatever was in the field, including a figure the rep typed: choosing a
   * band is a later, more deliberate statement of volume than the count that
   * happened to be there before. Retyping the count afterwards still stands —
   * nothing here reaches back in.
   *
   * "Auto" is the exception, and the reverse: there the count is what picks the
   * band, so the field is left exactly as it is.
   */
  const pickPackage = (value) => {
    /* Picking a plan is now a plain choice — it no longer rewrites the trademark
       count (the rep sets that independently, so 100 marks can sit under Startup).
       Switching plans clears any custom per-trademark price and feature picks, so
       the new plan starts from its own rate-card minimum with all features on. */
    set({ package: value, manual_rate: "", selected_feature_ids: null });
  };

  /* Features start all-ticked (selected_feature_ids null). The first untick
     snapshots the full list and drops the one toggled; re-ticking adds it back. */
  const toggleFeature = (id) => {
    const all = (quote?.plan_details?.features || []).map((f) => f.id);
    const current = form.selected_feature_ids == null ? all : form.selected_feature_ids;
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    set({ selected_feature_ids: next });
  };

  /* Stable per visit: the quotation number must not change on every keystroke
     while the document re-renders. */
  const quoteNo = useMemo(
    () => `EF/Q/${new Date().getFullYear()}/${Math.floor(1000 + Math.random() * 9000)}`,
    [],
  );

  /* Travels with the log row, where it is what makes a send read as expired —
     computed from the same validity the document prints, so the two agree. */
  const validityDays = template?.validity_days ?? 30;
  const validUntil = new Date(Date.now() + validityDays * 864e5).toISOString().slice(0, 10);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  /* What the API allowed, which is not always what was typed — the caps live on
     the server and this is the only honest source for them. */
  const applied = {
    additional: quote?.discounts?.additional_percent,
    onboarding: quote?.discounts?.onboarding_percent,
  };

  /* Nothing to send until the document actually renders — until then the
     preview is the "add the prospect's details" placeholder, and sending would
     mail that. The same list the document itself checks. */
  const missing = missingClientFields(form);
  /* A rep-entered price below the plan's rate-card floor is never allowed — it is
     what gets charged, so the send is blocked until it is at or above the minimum. */
  const manualNum = Number(form.manual_rate);
  const manualInvalid = form.manual_rate !== "" && rateMin != null
    && Number.isFinite(manualNum) && manualNum < rateMin;
  const canSend = Boolean(quote) && !quote.custom && missing.length === 0 && !manualInvalid;
  const sendBlockedWhy = missing.length
    ? `Fill in ${missing.join(", ")} first`
    : !quote ? "Waiting for pricing"
    : quote.custom ? "Enterprise — custom quote, contact sales"
    : manualInvalid ? `Price per trademark is below the ₹${rateMin} minimum`
    : undefined;

  /* What the quoted band includes, straight off the quote — the rate card owns
     these numbers, so nothing here restates them. `free_clients: -1` is the
     stored way of saying unlimited. */
  const li = quote?.line_items || {};
  const includedUsers = li.included_users;
  const extraUsers = Number(li.extra_users) || 0;
  const freeClients = li.free_clients;
  const extraClients = Number(li.extra_clients) || 0;
  const addons = Number(li.addons_monthly) || 0;

  /* A band's included seats and clients come with it whether or not anyone asks
     for them, so the form cannot sit below them — and the document prints these
     numbers, so leaving 1 there told the client they were getting one seat when
     the band gives five. Raised to the allowance, never lowered past it; asking
     for more than is included is the rep's call and is left alone. */
  useEffect(() => {
    if (!quote) return;
    const patch = {};

    /* Untouched, the field is the band's own allowance — following it down as
       well as up, or a bigger band's leftover would quietly bill extra seats on
       a smaller one. Touched, only the floor applies: what is included comes
       with the band whatever anyone types. */
    const align = (touched, included, current) => {
      if (included == null || Number(included) === -1) return null;
      const floor = Number(included);
      if (!touched) return Number(current) === floor ? null : floor;
      return Number(current) < floor ? floor : null;
    };

    const users = align(seatsTouched.current, includedUsers, form.users);
    if (users != null) patch.users = users;

    if (usesClients) {
      const clients = align(clientsTouched.current, freeClients, form.clients);
      if (clients != null) patch.clients = clients;
    }

    if (Object.keys(patch).length) set(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includedUsers, freeClients, usesClients, quote]);

  const allowance = (included, extra, unit) => {
    if (included == null) return null;
    if (Number(included) === -1) return "Unlimited included";
    const parts = [`${Number(included).toLocaleString("en-IN")} included`];
    if (extra > 0) parts.push(`${extra.toLocaleString("en-IN")} extra ${unit}`);
    return parts.join(" · ");
  };

  /* The dropdown shows what was quoted: on "auto" the API picks the tier, and
     the rep should see which one before they send it. */
  const selectedPackage =
    form.package === "auto" && quote?.plan ? quote.plan : form.package;

  const switchSegment = (segment) => {
    if (segment === form.segment) return;
    /* A band chosen in another segment does not exist here, so the pick resets
       to auto and the API re-suggests one for this segment's own bands. */
    seatsTouched.current = false;
    clientsTouched.current = false;
    /* The other segment's bands cover different volumes, so a count carried over
       from them is meaningless here. Cleared, and the effect above fills in this
       segment's own opening figure once its bands arrive. */
    marksTouched.current = false;
    const entering = segments.find((x) => x.slug === segment);
    /* Attorney numbers mean nothing to a corporate quote, and a stale list would
       keep filling in a count nobody asked it to. */
    const dropsAttorney = entering && Number(entering.is_attorney) === 0;
    set({
      segment,
      /* The opening-defaults effect pins this to the new segment's smallest plan
         once its bands load. */
      package: "auto",
      ...(dropsAttorney ? { attorney_numbers: [] } : {}),
      /* A new segment prices on its own rate card, so the custom per-trademark
         price and feature picks from the old one no longer apply. */
      manual_rate: "",
      selected_feature_ids: null,
      /* Back to the new band's own allowance, which the effect above fills in
         once the quote lands. */
      users: 1,
      clients: 0,
      marks: "",
    });
  };

  /* The only check worth making here: everything else about the volume — the
     minimum, the bands, the custom ceiling — is the API's answer to give. */
  const marksError = Number(form.marks) ? null : "Enter the number of trademarks";

  /* The counter drops responses that arrive after a newer request, so fast
     typing cannot leave stale pricing on screen. */
  const runCalculate = async () => {
    const id = ++requestRef.current;
    setCalculating(true);
    try {
      const data = await calculateProposal({
        segment: form.segment,
        marks: Number(form.marks) || 0,
        package: form.package,
        users: Number(form.users) || 1,
        clients: usesClients ? Number(form.clients) || 0 : 0,
        frequency: form.frequency,
        additional_discount: Number(form.additional_discount) || 0,
        onboarding_discount: Number(form.onboarding_discount) || 0,
        // 0 = no override → the server uses the rate card's own rate.
        manual_rate: Number(form.manual_rate) || 0,
      });
      if (id !== requestRef.current) return;
      setQuote(data);
      // Remember this plan's per-trademark floor so the field can validate against it.
      if (data?.line_items?.per_mark_rate_min != null) {
        setRateMin(data.line_items.per_mark_rate_min);
        setRateUnit(data.line_items.per_mark_rate_unit || null);
      }
      setCalcError("");
    } catch (err) {
      if (id !== requestRef.current) return;
      setQuote(null);
      setCalcError(err?.message || "Could not calculate this quote");
    } finally {
      if (id === requestRef.current) setCalculating(false);
    }
  };

  /**
   * The preview is already live, so re-pricing on demand changes nothing a rep
   * can see. What this button is actually for is the two cases the live path
   * cannot cover: retrying after a failed call, and putting the finished
   * document in front of you when the form has scrolled it out of sight.
   */
  const generate = () => {
    if (!marksError) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      runCalculate();
    }
    docRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /* One debounced calculate per settled edit. A below-minimum price is not sent —
     the last valid quote stays on screen and the inline error + blocked send do
     the talking, rather than wiping the preview on a 422. */
  useEffect(() => {
    if (marksError) { setQuote(null); setCalcError(marksError); return undefined; }
    if (manualInvalid) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(runCalculate, DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.segment, form.marks, form.package, form.users, form.clients,
    form.frequency, form.additional_discount, form.onboarding_discount,
    form.manual_rate, manualInvalid, marksError,
  ]);


  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <style>{PROPOSAL_CSS}</style>

          <div className="pg-scope">
            <div className="app">

              {/* ── FORM ── */}
              <div className="formcol">
              <div className="panel">
                {/* Back to the log, which is where this module starts. */}
                <Link
                  to="/cms/proposals"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 700, color: "var(--ind)", textDecoration: "none", marginBottom: 12 }}
                >
                  <ArrowLeftOutlined /> Back to Proposals &amp; Quotations
                </Link>
                <div className="brand">
                  <span className="logo">E</span> {template?.company?.brand_name || "Entermark"}
                  <span style={{ color: "var(--mut2)", fontSize: 11, fontWeight: 600, textTransform: "capitalize" }}>{doc}</span>
                </div>
                <p className="sub" style={{ marginTop: 10 }}>
                  Fill the inputs — pricing and the document update as you type.
                </p>

                {/* Two segments fit side by side; more than that wraps rather
                    than squeezing the labels. */}
                <div
                  className="seg"
                  style={segments.length > 2 ? { gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" } : undefined}
                >
                  {segments.map((s) => (
                    <button
                      key={s.slug}
                      className={form.segment === s.slug ? "on" : ""}
                      onClick={() => switchSegment(s.slug)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>

                <label>Client / company name *</label>
                <input value={form.client_name} onChange={(e) => set({ client_name: e.target.value })} placeholder="e.g. NovaPay Technologies" />

                <div className="row2">
                  <div>
                    <label>Contact name *</label>
                    <input value={form.contact_name} onChange={(e) => set({ contact_name: e.target.value })} placeholder="Priya Sharma" />
                  </div>
                  <div>
                    <label>Contact email *</label>
                    <input value={form.email} onChange={(e) => set({ email: e.target.value })} placeholder="priya@novapay.in" />
                  </div>
                </div>

                <label>Contact phone *</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    value={form.country_code}
                    onChange={(e) => set({ country_code: e.target.value })}
                    style={{ width: 118, flexShrink: 0 }}
                    aria-label="Country code"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  {/* Digits only, and no more than ten. The code lives in the
                       select beside it, and everything downstream — the lookup,
                       the send, the document — expects the two apart. */}
                  <input
                    value={form.phone}
                    onChange={(e) => set({ phone: e.target.value.replace(/\D/g, "").slice(-10) })}
                    placeholder="98xxx xxxxx"
                    inputMode="numeric"
                    style={{ flex: 1 }}
                  />
                </div>

                <div className="row2">
                  <div>
                    <label>Client GSTIN</label>
                    <input value={form.gstin} onChange={(e) => set({ gstin: e.target.value })} placeholder="optional" />
                  </div>
                  <div>
                    <label>Billing address</label>
                    <input value={form.address} onChange={(e) => set({ address: e.target.value })} placeholder="City, State, PIN" />
                  </div>
                </div>

                {/* A corporate client has no attorney number to look up — they
                    hold their own marks. The count below stays either way. */}
                {isAttorneySegment && (
                <AttorneyNumbers
                  numbers={form.attorney_numbers}
                  onChange={(attorney_numbers) => set({ attorney_numbers })}
                  onResult={({ total, details }) => {
                    setAttorneyTrademarks(details);
                    // A cleared list leaves the count alone rather than zeroing it.
                    if (total != null) {
                      /* What the registry actually holds beats any band's
                         nominal ceiling — this is the client's real book. */
                      marksTouched.current = true;
                      set({ marks: total });
                    }
                  }}
                />
                )}

                <label>
                  Trademarks{" "}
                  {minMarks != null && (
                    <span style={{ fontWeight: 400, color: "var(--mut2)" }}>
                      ({minMarks.toLocaleString("en-IN")}
                      {maxMarks != null ? ` – ${maxMarks.toLocaleString("en-IN")}` : "+"})
                    </span>
                  )} *
                </label>
                {/* Editing the count hands the band back to the API. The two
                    fields drive each other: a band fills in the volume it
                    covers, and changing that volume re-suggests the band — so
                    the rep can raise or lower it afterwards and watch the
                    package follow, instead of being pinned to the one they
                    picked a moment ago. */}
                <input
                  type="number" value={form.marks}
                  onChange={(e) => {
                    marksTouched.current = true;
                    set({ marks: e.target.value });
                  }}
                />
                {marksError && <div className="warn">{marksError}</div>}

                <label>Package</label>
                <select value={selectedPackage} onChange={(e) => pickPackage(e.target.value)}>
                  {packages.length === 0
                    ? <option value="auto">Auto — recommend by count</option>
                    : packages.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                {form.package === "auto" && quote?.plan && (
                  <div style={{ fontSize: 11.5, color: "var(--mut2)", lineHeight: 1.5, marginTop: 6, marginBottom: 12 }}>
                    Suggested by trademark count. Pick another band to re-price on it.
                  </div>
                )}

                {/* Per-trademark price — the rep's to set, but never below the
                    plan's rate-card minimum. Blank uses the card's own rate; the
                    whole quote (discount, GST, totals) re-prices on this number. */}
                {quote?.line_items?.per_mark_rate != null && (
                  <>
                    <label>
                      Price per trademark{" "}
                      <span style={{ fontWeight: 400, color: "var(--mut2)" }}>
                        (₹ {rateUnit === "yr" ? "/ mark / year" : "/ mark / month"})
                      </span>
                    </label>
                    <input
                      type="number" min={rateMin ?? 0} step="0.01"
                      value={form.manual_rate}
                      placeholder={`${rateMin ?? quote.line_items.per_mark_rate} (plan rate)`}
                      onChange={(e) => set({ manual_rate: e.target.value })}
                    />
                    {manualInvalid ? (
                      <div className="warn">
                        Minimum ₹{rateMin}{rateUnit === "yr" ? "/mark/yr" : "/mark/mo"} — you cannot price below the plan rate.
                      </div>
                    ) : (
                      <div style={{ fontSize: 11.5, color: "var(--mut2)", lineHeight: 1.5, marginTop: 6 }}>
                        Minimum ₹{rateMin ?? quote.line_items.per_mark_rate_min}{rateUnit === "yr" ? "/mark/yr" : "/mark/mo"}. Leave blank for the plan rate, or raise it to charge more.
                      </div>
                    )}
                  </>
                )}

                {/* Which plan features to print in the document and grant on
                    activation. All ticked by default — untick any to exclude it. */}
                {quote?.plan_details?.features?.length > 0 && (() => {
                  const feats = quote.plan_details.features;
                  const isOn = (id) => form.selected_feature_ids == null || form.selected_feature_ids.includes(id);
                  const onCount = feats.filter((f) => isOn(f.id)).length;
                  /* Show this quote's real counts in the labels (e.g. "Monitor up to
                     100 trademarks" for 100 marks) — same swap the document does, so
                     the picker and the printed proposal read identically. */
                  const counts = {
                    users: Number(form.users) || null,
                    clients: form.clients === "" ? null : Number(form.clients),
                    marks: quote?.marks ?? (Number(form.marks) || null),
                  };
                  return (
                    <div className="featpick">
                      <div className="fphead">
                        <label style={{ margin: 0 }}>
                          Plan features{" "}
                          <span style={{ fontWeight: 400, color: "var(--mut2)" }}>· untick any to exclude</span>
                        </label>
                        <span className="fpcount">{onCount} of {feats.length}</span>
                      </div>
                      <div className="featbox">
                        {feats.map((f) => {
                          const checked = isOn(f.id);
                          return (
                            <label key={f.id} className={`featrow ${checked ? "on" : "off"}`}>
                              <input type="checkbox" checked={checked} onChange={() => toggleFeature(f.id)} />
                              <span>{applyCounts(f.name, counts)}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                <label>
                  Number of users / seats{" "}
                  {allowance(includedUsers, extraUsers, "seat") && (
                    <span style={{ fontWeight: 400, color: "var(--mut2)" }}>
                      · {allowance(includedUsers, extraUsers, "seat")}
                    </span>
                  )}
                </label>
                <input
                  type="number" min="1" value={form.users}
                  onChange={(e) => { seatsTouched.current = true; set({ users: e.target.value }); }}
                />

                {usesClients && (
                  <>
                    <label>
                      Clients{" "}
                      {allowance(freeClients, extraClients, "client") && (
                        <span style={{ fontWeight: 400, color: "var(--mut2)" }}>
                          · {allowance(freeClients, extraClients, "client")}
                        </span>
                      )}
                    </label>
                    <input
                      type="number" min="0" value={form.clients}
                      onChange={(e) => { clientsTouched.current = true; set({ clients: e.target.value }); }}
                    />
                  </>
                )}
                {/* One line for what the extras actually cost, rather than a
                    per-field guess at it. */}
                {addons > 0 && (
                  <div style={{ fontSize: 11.5, color: "var(--amb)", lineHeight: 1.5, marginTop: 6 }}>
                    Add-ons add <b>{inr(addons)}</b>/month to this quote.
                  </div>
                )}

                <label>Payment frequency</label>
                <select value={form.frequency} onChange={(e) => set({ frequency: e.target.value })}>
                  {/* Monthly carries no prepay discount — there is nothing prepaid. */}
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annually</option>
                </select>


                <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--line)" }}>
                  <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--mut2)" }}>
                    Internal — not shown to the client
                  </div>
                </div>

                {/* In hours, not days: a link a rep wants dead by the end of
                    the day cannot be expressed in whole days at all. */}
                <label>
                  Payment link expiry (hours){" "}
                  <span style={{ fontWeight: 400, color: "var(--mut2)" }}>
                    · link stops accepting payment after this
                  </span>
                </label>
                <input
                  type="number" min="1" step="1" value={form.payment_link_expiry_hours}
                  onChange={(e) => set({ payment_link_expiry_hours: e.target.value })}
                  placeholder="e.g. 48"
                />
                <div style={{ fontSize: 11, color: "var(--mut2)", lineHeight: 1.5, marginTop: 6 }}>
                  Counted from when the {doc} is sent. Leave blank to use the
                  default{defaultExpiryHours ? ` (${hoursLabel(defaultExpiryHours)})` : ""}.
                </div>

                <label>
                  Grace period (days){" "}
                  <span style={{ fontWeight: 400, color: "var(--mut2)" }}>
                    · stays active this long past expiry
                  </span>
                </label>
                <input
                  type="number" min="0" step="1" value={form.grace_period_days}
                  onChange={(e) => set({ grace_period_days: e.target.value })}
                  placeholder="7"
                />

                <label>
                  Renewal reminder before (days){" "}
                  <span style={{ fontWeight: 400, color: "var(--mut2)" }}>
                    · daily countdown starts this early
                  </span>
                </label>
                <input
                  type="number" min="0" step="1" value={form.reminder_before_days}
                  onChange={(e) => set({ reminder_before_days: e.target.value })}
                  placeholder="7"
                />
                <div style={{ fontSize: 11, color: "var(--mut2)", lineHeight: 1.5, marginTop: 6 }}>
                  Carried into every renewal — set once. Leave blank to use the default.
                </div>

                {/* The cap is the backend's; what it actually applied is echoed
                    back, so a rep who asks for more sees what was allowed. */}
                <label>Additional discount % (rep discretion)</label>
                <input
                  type="number" min="0" value={form.additional_discount}
                  onChange={(e) => set({ additional_discount: Number(e.target.value) || 0 })}
                />
                {applied.additional != null && applied.additional !== Number(form.additional_discount) && (
                  <div className="warn">Capped at {applied.additional}% — that is what this quote uses.</div>
                )}

                <label>Onboarding discount % (can be waived fully)</label>
                <input
                  type="number" min="0" value={form.onboarding_discount}
                  onChange={(e) => set({ onboarding_discount: Number(e.target.value) || 0 })}
                />
                {applied.onboarding != null && applied.onboarding !== Number(form.onboarding_discount) && (
                  <div className="warn">Capped at {applied.onboarding}% — that is what this quote uses.</div>
                )}

                <label>Notes for the {doc} (optional)</label>
                <textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Any custom line…" />

                {/* The document re-renders as you type; this re-prices after a
                    failed call and jumps to the finished page. */}
                <button className="btn" onClick={generate}>
                  ⚡ Generate {doc}
                </button>

                <ExistingCustomerNotice customer={existingCustomer} />

                {/* Over the Cashfree single-payment cap → the rep is told here, at pricing
                    time, that this quote must go out as installments (set in the Send dialog). */}
                {quote?.payment_cap?.ok === false && (
                  <div style={{ margin: "4px 0 2px", padding: "10px 12px", borderRadius: 8, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", fontSize: 12.5, lineHeight: 1.5 }}>
                    <b>Cashfree limit:</b> this quote is {inr(quote.payment_cap.amount)} — more than Cashfree accepts in one payment{quote.payment_cap.max ? ` (max ${inr(quote.payment_cap.max)})` : ""}. On <b>Send</b>, choose <b>Installments</b> and split it into payments that add up to exactly {inr(quote.payment_cap.amount)}.
                  </div>
                )}

                {/* Emails the document and logs it in one step — printing and
                    copying tell nobody that anything went out. */}
                <button
                  className="btn sec2"
                  onClick={() => setSending("email")}
                  disabled={!canSend}
                  title={sendBlockedWhy}
                  style={!canSend ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                >
                  ✉ Send to client
                </button>
              </div>
              </div>

              {/* ── PREVIEW ── */}
              <div className="stage">
                <div className="toolbar">
                  <span className="t">
                    {isQuotation ? "Quotation preview" : "Proposal preview"}
                    {calculating && <span style={{ color: "var(--ind)" }}> · calculating…</span>}
                  </span>
                  <div className="acts">
                    <button onClick={copyHtml}>{copied ? "Copied!" : "Copy HTML"}</button>
                    <button
                      className="p"
                      onClick={() => setSending("download")}
                      disabled={!canSend}
                      title={sendBlockedWhy}
                      style={!canSend ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                    >
                      🖨 Download PDF
                    </button>
                    <button
                      className="p"
                      onClick={() => setSending("email")}
                      disabled={!canSend}
                      title={sendBlockedWhy}
                      style={!canSend ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
                    >
                      ✉ Send to client
                    </button>
                  </div>
                </div>

                <div ref={docRef}>
                  {calcError && !marksError ? (
                    <div className="empty" style={{ color: "var(--red)" }}>{calcError}</div>
                  ) : templateLoading ? (
                    <div className="empty">Loading the document template…</div>
                  ) : (
                    isQuotation ? (
                      <QuotationDocument
                        form={form} quote={quote} template={template} quoteNo={quoteNo}
                        segmentLabel={currentSegment?.label}
                      />
                    ) : (
                      <ProposalDocument
                        form={form} quote={quote} template={template} quoteNo={quoteNo}
                        segmentLabel={currentSegment?.label}
                        selectedFeatureIds={form.selected_feature_ids}
                      />
                    )
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Rendered but not shown: only its markup is wanted, and the server does
          the drawing. Kept out of the visible tree so it cannot affect layout. */}
      {!isQuotation && quotationTemplate && quote && (
        <div ref={quotationRef} style={{ display: "none" }} aria-hidden="true">
          <QuotationDocument
            form={form} quote={quote} template={quotationTemplate} quoteNo={quoteNo}
            segmentLabel={currentSegment?.label}
          />
        </div>
      )}

      {sending && (
        <SendModal
          doc={doc}
          form={form}
          quote={quote}
          validUntil={validUntil}
          validityDays={validityDays}
          brandName={template?.company?.brand_name}
          docNode={docRef.current}
          quotationNode={isQuotation ? null : quotationRef.current}
          existingCustomer={existingCustomer}
          attorneyTrademarks={attorneyTrademarks}
          initialChannel={sending}
          onClose={() => setSending(null)}
        />
      )}
    </div>
  );
};

export default CreateProposal;
